import { useStock } from './StockContext';
import { useClient } from './ClientContext';
import { useAlert } from '@/hooks/index';
import { useDbFunctions, useEvents } from '@/talons';
import { useTables } from './TablesContext';
import { useDiscountFlex } from './DiscountFlexContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ClientProps, StockProps } from '@/types';
import { getOnStorage, removeFromStorage, setOnStorage } from '@/shared';
import React, {
  createContext,
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// milisegundos * segundos * minutos * horas * dias
const MAX_TIME_TO_KEEP_ORDER_NUMBER = 1000 * 60 * 60 * 24 * 8;

export interface CartItems {
  sku: number;
  uid: string;
  quantity: number;
  option_uid: string;
  category_id: number;
  addedAt?: string;
}

export interface CartDetailsProps {
  id?: string;
  total?: number;
  quantity: number;
  subtotal?: number;
  items: CartItems[];
  rcaCod?: number;
}

export interface CartProps {
  cartId: string;
  cart: CartItems[];
  filterTerm: string;
  isSearching: boolean;
  // estoque: StockProps[];
  totalQuantity: number;
  totalVolume: number;
  cartDetails: Record<number, CartDetailsProps>;
  isAdding: boolean;
  hasOrderNumberSaved: boolean;
  updateLastOrderSendTime: () => void;

  checkLimit: (uid: string, sku: number, quantity: number, updatedCart: CartItems[], showAlert?: boolean) => number;
  addProductToCart: (data: { sku: number; quantity: number; option_uid: string; uid: string; category_id: number }) => void;
  updateProductAmount: (data: { uid: string; sku: number; quantity: number }) => void;
  clearCart: () => void;
  handleRemoveItems: () => void;
  getLimit: (sku: number) => number;
  getStockMsg: (sku: number) => string | undefined;
  setFilterTerm: Dispatch<SetStateAction<string>>;
  removeProductFromCart: ({ uid }: { uid: string }) => void;
  removeMultipleProductsFromCart: (uids: string[]) => void;
  createEmptyCart: ({ client }: { client: ClientProps }) => void;
  setIsSearching: Dispatch<SetStateAction<boolean>>;
  loadedEstoque: RefObject<boolean>;
  setCartLoading: Dispatch<SetStateAction<boolean>>;
  cartLoading: boolean;
  setCartSort: Dispatch<SetStateAction<'inclusao' | 'alfabetica' | 'inclusao-antigos'>>;
  cartSort: 'inclusao' | 'alfabetica' | 'inclusao-antigos';
}

export const CartContext = createContext({} as CartProps);

const CartProvider = ({ children }: React.PropsWithChildren) => {
  const { tables } = useTables();
  const { stockLeft } = useStock();
  const db = useDbFunctions();
  const { registerEvent } = useEvents(db);
  const { selectedClient } = useClient();
  const { showAlert } = useAlert();
  const { clearClientDiscount } = useDiscountFlex();

  const loadedEstoque = useRef(false);

  const [isAdding, setIsAdding] = useState(false);
  const [filterTerm, setFilterTerm] = useState('');
  const [cartSort, setCartSort] = useState<'inclusao' | 'alfabetica' | 'inclusao-antigos'>(() => {
    const saved = getOnStorage('cart_sort');
    return saved === 'inclusao' || saved === 'alfabetica' || saved === 'inclusao-antigos' ? saved : 'alfabetica';
  });
  const [cartLoading, setCartLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const [cart, setCart] = useState<CartItems[]>(() => {
    const savedCart = getOnStorage('cart_details');
    return savedCart?.[selectedClient?.COD_CLIENTE]?.items ?? [];
  });

  const [cartDetails, setCartDetails] = useState<Record<number, CartDetailsProps>>(() => {
    return getOnStorage('cart_details') || {};
  });

  const [cartId, setCartId] = useState(() => getOnStorage('cart_id') || '');
  const [hasOrderNumberSaved, setHasOrderNumberSaved] = useState(false);

  const updateLastOrderSendTime = useCallback(() => {
    if (!selectedClient?.COD_CLIENTE) return;

    const storedOrders = getOnStorage('order_numbers') || {};
    const lastUpdate = new Date().getTime();
    setOnStorage('order_numbers', {
      ...storedOrders,
      [selectedClient.COD_CLIENTE]: { orderNumber: storedOrders[selectedClient.COD_CLIENTE]?.orderNumber, lastUpdate },
    });
  }, [selectedClient]);

  const dictEstoque = useMemo(() => {
    if (!tables.current?.estoque || !selectedClient?.COD_LOJA) return {};

    const estoqueMap = new Map<number, StockProps>();
    for (const item of tables.current.estoque) {
      if (item.COD_LOJA === selectedClient.COD_LOJA) {
        estoqueMap.set(item.COD_PRODUTO, item);
      }
    }
    return Object.fromEntries(estoqueMap);
  }, [tables.current?.estoque, selectedClient?.COD_LOJA, stockLeft]);

  const totalQuantity = useMemo(() => {
    return cartDetails?.[selectedClient?.COD_CLIENTE]?.items?.length || 0;
  }, [cartDetails, selectedClient?.COD_CLIENTE]);

  const totalVolume = useMemo(() => {
    const items = cartDetails?.[selectedClient?.COD_CLIENTE]?.items || [];
    return items.reduce((acc, item) => acc + (item.quantity || 0), 0);
  }, [cartDetails, selectedClient?.COD_CLIENTE]);

  const getLimit = useCallback(
    (id: number) => {
      return dictEstoque[id]?.QTD_ESTOQUE || 0;
    },
    [dictEstoque]
  );

  const getStockMsg = useCallback((id: number) => dictEstoque[id]?.MSG_ESTOQUE, [dictEstoque]);

  const removeProductFromCart = useCallback(
    async ({ uid }: { uid: string }) => {
      if (!selectedClient?.COD_CLIENTE) return;
      
      updateLastOrderSendTime();
      const clientCart = cartDetails[selectedClient.COD_CLIENTE];
      if (!clientCart?.items) return;
      
      registerEvent({
        tipoEvento: 'CART',
        obsEvento: `[removeProductFromCart]: UID: ${uid}`,
      });
      const newCart = clientCart.items.filter((product) => product.uid !== uid);
      const total = newCart.reduce((acc, i) => acc + (i?.quantity || 0), 0);

      const updatedCartDetails = {
        ...cartDetails,
        [selectedClient.COD_CLIENTE]: {
          items: newCart,
          quantity: total,
        },
      };

      setCart(newCart);
      setCartDetails(updatedCartDetails);
      setOnStorage('cart_details', updatedCartDetails);
    },
    [cartDetails, selectedClient?.COD_CLIENTE, updateLastOrderSendTime]
  );

  const removeMultipleProductsFromCart = useCallback(
    (uids: string[]) => {
      if (!selectedClient?.COD_CLIENTE || !uids?.length) return;

      const clientCart = cartDetails[selectedClient.COD_CLIENTE];
      if (!clientCart?.items?.length) return;

      const newCart = clientCart.items.filter((product) => !uids.includes(product.uid));
      if (newCart.length === clientCart.items.length) return;

      const total = newCart.reduce((acc, i) => acc + (i?.quantity || 0), 0);

      const updatedCartDetails = {
        ...cartDetails,
        [selectedClient.COD_CLIENTE]: {
          items: newCart,
          quantity: total,
        },
      };

      setCartDetails(updatedCartDetails);
      setOnStorage('cart_details', updatedCartDetails);
    },
    [cartDetails, selectedClient?.COD_CLIENTE]
  );

  const checkLimit = useCallback(
    (uid: string, sku: number, quantity: number, updatedCart: CartItems[]): number => {
      const packQtd = Number(uid?.split('-')[2]);
      const initialQtd = quantity * packQtd;

      let totalAmount = initialQtd;
      for (const item of updatedCart) {
        if (item.sku === sku && uid !== item.uid) {
          const [, qtd] = item.option_uid.split('_');
          totalAmount += item.quantity * Number(qtd);
        }
      }

      const limit = getLimit(sku);
      const stock = stockLeft?.[sku]?.stock_left ?? 0;
      const hasStockInfo = stockLeft?.[sku]?.hasOwnProperty('stock_left');
      const availableQty = hasStockInfo ? stock : limit;

      if (availableQty < totalAmount) {
        const remainingQty = availableQty - (totalAmount - initialQtd);
        return Math.max(0, Math.trunc(remainingQty / packQtd));
      }

      return quantity;
    },
    [getLimit, stockLeft]
  );

  const saveOrderNumber = useCallback((client: ClientProps, orderNumber: string) => {
    if (!client?.COD_CLIENTE) return;

    const storedOrders = getOnStorage('order_numbers') || {};
    const lastUpdate = new Date().getTime();
    setOnStorage('order_numbers', {
      ...storedOrders,
      [client.COD_CLIENTE]: { orderNumber, lastUpdate },
    });
  }, []);

  const generateOrderNumber = useCallback(() => {
    if (!selectedClient?.COD_CLIENTE) return '';

    const now = new Date();
    const cartOpenedAt = format(now, 'yyyy-MM-dd HH:mm:ss', { locale: ptBR });
    const seqpessoa = selectedClient.COD_RCA;
    const date = now.getDate().toString().padStart(2, '0');
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const year = now.getFullYear();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');

    const orderNumber = `${seqpessoa}${date}${month}${year}${hours}${minutes}${seconds}`;
    setOnStorage('cart_opened_at', cartOpenedAt);
    saveOrderNumber(selectedClient, orderNumber);

    return orderNumber;
  }, [selectedClient, saveOrderNumber]);

  const getOrGenerateOrderNumber = useCallback(() => {
    if (!selectedClient?.COD_CLIENTE) return '';

    const storedOrders = getOnStorage('order_numbers') || {};
    const orderNumber = storedOrders[selectedClient.COD_CLIENTE]?.orderNumber || generateOrderNumber();

    if (!storedOrders[selectedClient.COD_CLIENTE]?.orderNumber) {
      saveOrderNumber(selectedClient, orderNumber);
    }

    return orderNumber;
  }, [selectedClient, generateOrderNumber, saveOrderNumber]);

  useEffect(() => {
    if (!selectedClient?.COD_CLIENTE) {
      setHasOrderNumberSaved(false);
      return;
    }

    const storedOrders = getOnStorage('order_numbers') || {};
    const clientCart = cartDetails[selectedClient.COD_CLIENTE];
    const clientOrder = storedOrders[selectedClient.COD_CLIENTE];

    // Se não existir orderNumber e houver itens no carrinho, gera um novo orderNumber
    if (!clientOrder?.orderNumber && clientCart && clientCart.items && clientCart.items.length > 0) {
      const newOrderNumber = generateOrderNumber();
      storedOrders[selectedClient.COD_CLIENTE] = { orderNumber: newOrderNumber };
      setOnStorage('order_numbers', storedOrders);
      setHasOrderNumberSaved(true);
    } else {
      setHasOrderNumberSaved(!!clientOrder?.orderNumber);
    }
  }, [selectedClient, cartDetails, generateOrderNumber]);

  const createEmptyCart = useCallback(({ client }: { client: ClientProps | undefined }) => {
    if (!client?.COD_CLIENTE) return;

    const cartId = String(client.COD_CLIENTE);
    setCartId(cartId);
    setOnStorage('cart_id', cartId);
  }, []);

  const addProductToCart = useCallback(
    async ({
      sku,
      quantity,
      option_uid,
      uid,
      category_id,
    }: {
      sku: number;
      quantity: number;
      option_uid: string;
      uid: string;
      category_id: number;
    }) => {
      if (!selectedClient?.COD_CLIENTE) {
        alert('Por favor, selecione um cliente para continuar');
        return;
      }

      registerEvent({
        tipoEvento: 'CART',
        codProduto: sku,
        obsEvento: `[addProductToCart]: SKU: ${sku} | OPTION_UID: ${option_uid} | QTD: ${quantity}`,
      });

      getOrGenerateOrderNumber();
      updateLastOrderSendTime();
      setIsAdding(true);

      setCartDetails((prevCartDetails) => {
        const clientCart = prevCartDetails[selectedClient.COD_CLIENTE] || { items: [] };
        const updatedCart = [...clientCart.items];

        quantity = checkLimit(uid, sku, quantity, updatedCart);
        if (quantity <= 0) return prevCartDetails;

        const existingProductIndex = updatedCart.findIndex((product) => product.sku === sku && product.uid === uid);

        const currentTime = new Date().toISOString();

        if (existingProductIndex >= 0) {
          updatedCart[existingProductIndex].quantity = quantity;
          updatedCart[existingProductIndex].addedAt = currentTime; // Save the time of addition
        } else {
          updatedCart.push({ uid, sku, quantity, option_uid, category_id, addedAt: currentTime });
        }

        const total = updatedCart.reduce((acc, i) => acc + (i?.quantity || 0), 0);
        const newCartDetails = {
          ...prevCartDetails,
          [selectedClient.COD_CLIENTE]: { items: updatedCart, quantity: total },
        };

        setOnStorage('cart_details', newCartDetails);
        return newCartDetails;
      });

      setIsAdding(false);
    },
    [selectedClient, checkLimit, getOrGenerateOrderNumber]
  );

  const updateProductAmount = useCallback(
    async ({ uid, sku, quantity }: { uid: string; sku: number; quantity: number }) => {
      if (!selectedClient?.COD_CLIENTE) {
        alert('Por favor, selecione um cliente para continuar');
        return;
      }

      getOrGenerateOrderNumber();
      updateLastOrderSendTime();
      const clientCart = cartDetails[selectedClient.COD_CLIENTE];
      if (!clientCart?.items) return;

      const updatedCart = [...clientCart.items];
      quantity = checkLimit(uid, sku, quantity, updatedCart);

      if (quantity < 0) return;

      const productIndex = updatedCart.findIndex((item) => item.sku === sku && item.uid === uid);
      
      const action = updatedCart[productIndex].quantity > quantity ? 'decrease' : 'increase';
      registerEvent({
        tipoEvento: 'CART',
        obsEvento: `[updateProductAmount]: SKU: ${sku} | UID: ${uid} | QTD: ${quantity} | ACTION: ${action}`,
      });

      if (productIndex >= 0) {
        updatedCart[productIndex].quantity = quantity;
        const total = updatedCart.reduce((acc, i) => acc + (i?.quantity || 0), 0);

        setCart(updatedCart);
        setCartDetails({
          ...cartDetails,
          [selectedClient.COD_CLIENTE]: {
            items: updatedCart,
            quantity: total,
          },
        });
        setOnStorage('cart_details', {
          ...cartDetails,
          [selectedClient.COD_CLIENTE]: {
            items: updatedCart,
            quantity: total,
          },
        });
      }
    },
    [cartDetails, selectedClient, checkLimit, getOrGenerateOrderNumber, stockLeft]
  );

  const clearCart = useCallback(() => {
    setCart([]);
    removeFromStorage('cart');
  }, []);

  const handleRemoveItems = useCallback(
    async (clientId?: number) => {
      if (!selectedClient?.COD_CLIENTE && !clientId) return;

      updateLastOrderSendTime();
      clearClientDiscount(clientId || selectedClient.COD_CLIENTE);
      setCartDetails({
        ...cartDetails,
        [clientId || selectedClient.COD_CLIENTE]: { items: [], quantity: 0 },
      });
      setOnStorage('cart_details', {
        ...cartDetails,
        [clientId || selectedClient.COD_CLIENTE]: { items: [], quantity: 0 },
      });

      const orders = (getOnStorage('order_numbers') as { [key: number]: { lastUpdate: number; orderNumber: string } }) || {};
      delete orders?.[(clientId || selectedClient.COD_CLIENTE) as number];
      setOnStorage('order_numbers', orders);
      registerEvent({
        tipoEvento: 'CART',
        obsEvento: `[handleRemoveItems]: CLEAR_CART`,
      });
    },
    [cartDetails, selectedClient?.COD_CLIENTE, clearClientDiscount]
  );

  useEffect(() => {
    if (!selectedClient?.COD_CLIENTE) {
      setHasOrderNumberSaved(false);
      return;
    }

    const storedOrders = getOnStorage('order_numbers') || {};
    setHasOrderNumberSaved(!!storedOrders[selectedClient.COD_CLIENTE]?.orderNumber);
  }, [selectedClient]);

  useEffect(() => {
    setOnStorage('cart_sort', cartSort);
  }, [cartSort]);

  useEffect(() => {
    const now = new Date().getTime();
    const orders = getOnStorage('order_numbers') || ({} as { lastUpdate: number; orderNumber: string });
    const listOrders = Object.keys(orders) as string[];

    for (const order of listOrders) {
      const shouldRemove = orders[order].lastUpdate < now - MAX_TIME_TO_KEEP_ORDER_NUMBER;
      if (shouldRemove) {
        const clientId = Number(order);
        handleRemoveItems(clientId);

        if (clientId !== selectedClient?.COD_CLIENTE) continue;

        showAlert({
          title: 'Carrinhos expirados foram removidos.',
          theme: 'warning',
          time: 5000,
          close: 'icon',
          bgType: 'primary',
          borderLeft: true,
          bordered: true,
        });
      }
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      cart,
      cartId,
      cartDetails,
      totalQuantity,
      totalVolume,
      addProductToCart,
      getLimit,
      getStockMsg,
      loadedEstoque,
      removeProductFromCart,
      removeMultipleProductsFromCart,
      updateProductAmount,
      createEmptyCart,
      clearCart,
      checkLimit,
      handleRemoveItems,
      isSearching,
      setIsSearching,
      filterTerm,
      setFilterTerm,
      isAdding,
      cartLoading,
      updateLastOrderSendTime,
      setCartLoading,
      hasOrderNumberSaved,
      setCartSort,
      cartSort,
    }),
    [
      cart,
      cartId,
      cartDetails,
      totalQuantity,
      totalVolume,
      getStockMsg,
      updateLastOrderSendTime,
      getLimit,
      checkLimit,
      addProductToCart,
      removeProductFromCart,
      removeMultipleProductsFromCart,
      updateProductAmount,
      createEmptyCart,
      clearCart,
      handleRemoveItems,
      isSearching,
      filterTerm,
      isAdding,
      cartLoading,
      hasOrderNumberSaved,
      cartSort,
    ]
  );

  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>;
};

const useCart = () => {
  const context = useContext(CartContext);

  if (!context) throw new Error('O context useCart só pode ser usado dentro de seu escopo. Erro ao usar hook useCart');

  return context;
};

export { CartProvider, useCart };
